import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth-store";
import { Plus, Trash2, ExternalLink } from "lucide-react";

type Filter = "upcoming" | "past" | "mine" | "all";

export default function MeetingsPage() {
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("from");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filter, setFilter] = useState<Filter>("upcoming");
  const [filtersOpen, setFiltersOpen] = useState(true);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (filter === "upcoming") p.upcoming = true;
    if (filter === "past")     p.past = true;
    if (filter === "mine" && me) p.userId = me.id;
    return p;
  }, [filter, me]);

  const { data, isLoading } = useList<any>("/meetings", {
    q, page, pageSize, sortBy, sortDir, ...params,
  });

  const rows = data?.data ?? [];
  const total = data?.pagination?.total;

  const groups: FilterGroup[] = [
    {
      title: "Views",
      items: [
        { label: "Upcoming", active: filter === "upcoming", onClick: () => setFilter("upcoming") },
        { label: "Past",     active: filter === "past",     onClick: () => setFilter("past") },
        { label: "My meetings", active: filter === "mine",  onClick: () => setFilter("mine") },
        { label: "All",      active: filter === "all",      onClick: () => setFilter("all") },
      ],
    },
  ];

  async function del(id: string, title: string) {
    if (!confirm(`Delete meeting "${title}"?`)) return;
    await api.delete(`/meetings/${id}`);
    qc.invalidateQueries({ queryKey: ["/meetings"] });
  }

  return (
    <>
      <PageHeader
        title="Meetings"
        description="Scheduled and past meetings across your accounts and contacts."
        actions={<Button asChild><Link to="/meetings/new"><Plus className="mr-1 h-4 w-4"/>Create Meeting</Link></Button>}
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "from",      label: "Start time" },
          { value: "createdAt", label: "Created" },
          { value: "subject",   label: "Title" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={<Input placeholder="Search meetings…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />}
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Title</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Venue</Th>
                  <Th>Related to</Th>
                  <Th>Host</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={7} className="py-12 text-center text-muted-foreground">No meetings in this view.</Td></Tr>
                )}
                {rows.map((m: any) => (
                  <Tr key={m.id} className="group">
                    <Td className="font-medium">{m.subject}</Td>
                    <Td className="text-sm">{m.scheduledAt ? new Date(m.scheduledAt).toLocaleString() : "—"}</Td>
                    <Td className="text-sm">{m.completedAt ? new Date(m.completedAt).toLocaleString() : "—"}</Td>
                    <Td className="text-muted-foreground">{m.venue ?? m.location ?? "—"}</Td>
                    <Td className="text-muted-foreground">
                      {m.contact?.name ? <Link to={`/contacts/${m.contact.id}`} className="hover:underline">{m.contact.name}</Link> :
                       m.deal?.name    ? m.deal.name : "—"}
                    </Td>
                    <Td className="text-muted-foreground">{m.user?.name ?? "—"}</Td>
                    <Td>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {m.meetingUrl && (
                          <a href={m.meetingUrl} target="_blank" rel="noreferrer" title="Join" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <ExternalLink className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        <button onClick={() => del(m.id, m.subject)} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-muted">
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}
